import {
  FundingNeedCreate,
  FundingNeedResponse,
  FundingNeedDataAssessmentResponse,
  FundingNeedUpdate,
  SponsorshipLetterData,
} from '../models/FundingNeeds'
import { throwIfProfileRegenerating } from './profileRegenError'

const serverURL = import.meta.env.VITE_API_URL

interface createFundingNeedFn {
  token: string
  fundingNeed: FundingNeedCreate
}

// Create a new funding need
export async function createFundingNeed({
  token,
  fundingNeed,
}: createFundingNeedFn): Promise<
  FundingNeedResponse | FundingNeedDataAssessmentResponse
> {
  const response = await fetch(`${serverURL}/funding-needs/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(fundingNeed),
  })

  if (!response.ok) {
    const errorDetails = await response.json()
    if (
      errorDetails.detail.includes(
        'Either a minimum amount or full amount must be provided'
      )
    ) {
      throw new Error('Either a minimum amount or full amount must be provided')
    }
    throw new Error(`Error creating funding need: ${response.statusText}`)
  }

  return response.json()
}

interface deleteFundingNeedFn {
  token: string
  fundingNeedId: number
}

// Delete a funding need
export async function deleteFundingNeed({
  token,
  fundingNeedId,
}: deleteFundingNeedFn): Promise<void> {
  const response = await fetch(`${serverURL}/funding-needs/${fundingNeedId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Error deleting funding need: ${response.statusText}`)
  }
}

interface updateFundingNeedFn {
  token: string
  fundingNeedId: number
  updates: FundingNeedUpdate
}

// Update a funding need
export async function updateFundingNeed({
  token,
  fundingNeedId,
  updates,
}: updateFundingNeedFn): Promise<
  FundingNeedResponse | FundingNeedDataAssessmentResponse
> {
  const response = await fetch(`${serverURL}/funding-needs/${fundingNeedId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  })

  if (!response.ok) {
    const errorDetails = await response.json()
    if (
      errorDetails.detail.includes(
        'Either a minimum amount or full amount must be provided'
      )
    ) {
      throw new Error('Either a minimum amount or full amount must be provided')
    }

    throw new Error(`Error updating funding need: ${response.statusText}`)
  }

  return response.json()
}

interface updateFundingNeedArchiveStatusFn {
  token: string
  fundingNeedId: number
}

// Update funding need archive status
export async function updateFundingNeedArchiveStatus({
  token,
  fundingNeedId,
}: updateFundingNeedArchiveStatusFn): Promise<FundingNeedResponse> {
  const response = await fetch(
    `${serverURL}/funding-needs/${fundingNeedId}/archive`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    throw new Error(
      `Error updating funding need archive status: ${response.statusText}`
    )
  }

  return response.json()
}

// Get all funding needs for an organisation
export async function getOrganisationFundingNeeds(
  token: string,
  orgUuid: string
): Promise<FundingNeedResponse[]> {
  const response = await fetch(
    `${serverURL}/funding-needs/organisation/${orgUuid}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    throw new Error(
      `Error fetching organisation funding needs: ${response.statusText}`
    )
  }

  return response.json()
}

export async function generateSponsorLetter(
  token: string,
  need_id: number,
  letter_data: SponsorshipLetterData
) {
  const response = await fetch(
    `${serverURL}/funding-needs/${need_id}/generate-sponsor-letter`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(letter_data),
    }
  )

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    return { error: 'Failed to generate sponsor letter' }
  }

  const data = await response.json()
  return data
}

export async function emailSponsorshipLetter(
  token: string,
  need_id: number,
  sponsor_name: string,
  letter: string
) {
  const response = await fetch(
    `${serverURL}/funding-needs/${need_id}/email-sponsor-letter`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sponsor_name, letter }),
    }
  )

  if (!response.ok) {
    return { success: false, error: 'Failed to email sponsor letter' }
  }

  return { success: true, error: null }
}
